/**
 * FavouritesContext — backed by /api/client/wishlist when authenticated.
 * Falls back to localStorage for guests.
 */
import { createContext, useReducer, useEffect, useContext, useCallback } from 'react'
import { useSnackbar } from 'notistack'
import { useAuth } from '../AuthContext'
import { clientApi, mediaUrl } from 'services/api'

const normItem = (item) => ({
  ...item,
  _id: String(item.product_id ?? item._id ?? item.id),
  title: item.name || item.title || '',
  price: parseFloat(item.sale_price || item.base_price || item.price || 0),
  originalPrice: parseFloat(item.base_price || item.price || 0),
  stock: item.stock_quantity ?? item.stock ?? 0,
  thumbnail: mediaUrl(item.thumbnail_url || item.primary_image || item.thumbnail),
  discountPercentage: 0,
})

const reducer = (state, action) => {
  const { type, payload } = action
  switch (type) {
    case 'LOADED': return { ...state, favourites: payload, isFavouritesLoading: false }
    case 'LOADING': return { ...state, isFavouritesLoading: payload }
    case 'RESET': return { ...state, favourites: [], isFavouritesLoading: false }
    default: return state
  }
}

const initialState = { favourites: [], isFavouritesLoading: false }
const FavouritesContext = createContext(initialState)

const FavouritesProvider = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar()
  const { isAuthenticated } = useAuth()
  const [state, dispatch] = useReducer(reducer, initialState)

  const fetchWishlist = useCallback(async () => {
    if (!isAuthenticated) {
      dispatch({ type: 'LOADED', payload: [] })
      return
    }
    dispatch({ type: 'LOADING', payload: true })
    try {
      const { data } = await clientApi.wishlist.get()
      dispatch({ type: 'LOADED', payload: (data?.data || []).map(normItem) })
    } catch {
      dispatch({ type: 'LOADED', payload: [] })
    }
  }, [isAuthenticated])

  useEffect(() => { fetchWishlist() }, [fetchWishlist])

  const addToFavourites = async (product, showNotification = true) => {
    if (!isAuthenticated) {
      showNotification && enqueueSnackbar('Please login to save favourites', { variant: 'info' })
      return
    }
    try {
      await clientApi.wishlist.add(Number(product._id || product.id))
      await fetchWishlist()
      showNotification && enqueueSnackbar(`${product.title || product.name} added to wishlist`, { variant: 'success' })
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Could not add to wishlist', { variant: 'error' })
    }
  }

  const removeFromFavourites = async (productId, showNotification = true) => {
    try {
      await clientApi.wishlist.remove(productId)
      await fetchWishlist()
      showNotification && enqueueSnackbar('Removed from wishlist', { variant: 'info' })
    } catch {
      enqueueSnackbar('Remove failed', { variant: 'error' })
    }
  }

  const resetFavourites = () => dispatch({ type: 'RESET' })

  const isFavourite = (productId) =>
    state.favourites.some((item) => String(item._id) === String(productId))

  return (
    <FavouritesContext.Provider
      value={{
        favourites: state.favourites,
        favouriteItemIds: state.favourites.map((item) => item._id),
        favouritesQuantity: state.favourites.length,
        isFavouritesLoading: state.isFavouritesLoading,
        addToFavourites,
        removeFromFavourites,
        resetFavourites,
        fetchWishlist,
        isFavourite,
        setFavouritesLoadingState: (b) => dispatch({ type: 'LOADING', payload: b }),
      }}
      displayName="Favourites"
    >
      {children}
    </FavouritesContext.Provider>
  )
}

const useFavourites = () => {
  const context = useContext(FavouritesContext)
  if (context === undefined) throw new Error('useFavourites can only be used inside FavouritesProvider')
  return context
}

export { FavouritesProvider, useFavourites }
